import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"

const MENU_MIN_WIDTH = 160
const MENU_ITEM_HEIGHT = 36
const MENU_VERTICAL_PADDING = 8
const VIEWPORT_PADDING = 8
const MENU_OFFSET = 4

const estimateMenuHeight = (itemCount, measuredHeight) => {
  if (measuredHeight > 0) return measuredHeight
  return itemCount * MENU_ITEM_HEIGHT + MENU_VERTICAL_PADDING
}

const TableActionsDropdown = ({ items = [] }) => {
  const [open, setOpen] = useState(false)
  const toggleRef = useRef(null)
  const menuRef = useRef(null)
  const [menuStyle, setMenuStyle] = useState({})

  const updatePosition = useCallback(() => {
    if (!toggleRef.current) return

    const rect = toggleRef.current.getBoundingClientRect()
    const menuWidth = menuRef.current?.offsetWidth || MENU_MIN_WIDTH
    const menuHeight = estimateMenuHeight(
      items.length,
      menuRef.current?.offsetHeight || 0
    )

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING
    const spaceAbove = rect.top - VIEWPORT_PADDING
    const openUpward = spaceBelow < menuHeight && spaceAbove >= spaceBelow

    let top = openUpward
      ? rect.top - menuHeight - MENU_OFFSET
      : rect.bottom + MENU_OFFSET
    top = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PADDING)
    )

    const isRtl =
      typeof document !== "undefined" &&
      (document.documentElement.getAttribute("dir") === "rtl" ||
        getComputedStyle(document.documentElement).direction === "rtl")

    // LTR: align the menu's trailing edge with the toggle.
    // RTL: align the menu's leading edge with the toggle so it stays under the card.
    let left = isRtl ? rect.left : rect.right - menuWidth
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PADDING)
    )

    setMenuStyle({
      "--table-actions-menu-top": `${top}px`,
      "--table-actions-menu-left": `${left}px`,
    })
  }, [items.length])

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
    const rafId = requestAnimationFrame(() => {
      updatePosition()
      requestAnimationFrame(updatePosition)
    })
    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener("scroll", updatePosition, true)
      window.removeEventListener("resize", updatePosition)
    }
  }, [open, updatePosition, items])

  useEffect(() => {
    if (!open) return
    const handleOutsideClick = event => {
      if (
        toggleRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return
      }
      setOpen(false)
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [open])

  const handleToggle = event => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(prev => !prev)
  }

  if (!items.length) return null

  return (
    <>
      <span
        ref={toggleRef}
        className="table-actions-dropdown-toggle d-inline-flex align-items-center"
        onClick={e => e.stopPropagation()}
      >
        <a
          href="#"
          className="card-drop d-inline-flex align-items-center"
          onClick={handleToggle}
          aria-expanded={open}
          aria-haspopup="true"
        >
          <i className="mdi mdi-dots-vertical font-size-18"></i>
        </a>
      </span>
      {open &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="dropdown-menu table-actions-dropdown-menu show"
            style={menuStyle}
          >
            {items.map((item, index) => (
              <button
                key={item.key ?? index}
                type="button"
                className={`dropdown-item${item.className ? ` ${item.className}` : ""}`}
                disabled={item.disabled}
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                  if (item.disabled) return
                  setOpen(false)
                  item.onClick?.(event)
                }}
              >
                {item.content}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}

export default TableActionsDropdown
